import Vue from 'vue';
import VueRouter from 'vue-router';

import config from '@/config';
import { loadTranslations } from '@/services/translations';

import store from '@/store';
import MainApp from '../MainApp.vue';
import AuthApp from '../AuthApp.vue';

const selectedLanguage = localStorage.getItem('langId') || 'bs';
// const selectedLanguage = 'bs';
const translations = loadTranslations(selectedLanguage);

Vue.use(VueRouter);

const routes = [
  {
    path: '/newsletter-success',
    name: 'NewsletterSuccess',
    meta: {
      title: 'Newsletter',
    },
    component: () => import(/* webpackChunkName: 'newsletter-success' */ '@/views/NewsletterConfirmSuccess.vue'),
  },
  {
    path: '/newsletter-confirm',
    name: 'NewsletterConfirm',
    meta: {
      title: 'Newsletter',
    },
    component: () => import(/* webpackChunkName: 'brand' */ '@/views/NewsletterConfirm.vue'),
  },
  {
    path: '/password-reset-success',
    name: 'PasswordResetSuccess',
    meta: {
      title: 'Obnovljena lozinka',
    },
    component: () => import(/* webpackChunkName: 'password-reset-success' */ '@/views/PasswordResetSuccess.vue'),
  },
  {
    path: '/auth',
    component: AuthApp,
    children: [
      {
        path: 'login',
        name: 'Login',
        meta: {
          title: translations.login,
        },
        component: () => import('@/components/Auth/Login.vue'),
      },
      {
        path: 'logout',
        name: 'LogOut',
        meta: {
          title: translations.logout,
        },
        component: () => import('@/components/Auth/LogOut.vue'),
      },
      {
        path: 'register',
        name: 'Register',
        meta: {
          title: translations.register,
        },
        component: () => import('@/components/Auth/Register.vue'),
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        meta: {
          title: translations.forgotPassword,
        },
        component: () => import('@/components/Auth/ForgotPassword.vue'),
      },
      {
        path: 'customer-password-reset',
        name: 'ResetPassword',
        meta: {
          title: 'Reset lozinke',
        },
        component: () => import('@/components/Auth/ResetPassword.vue'),
      },
      {
        path: 'verify-email',
        name: 'VerifyEmail',
        meta: {
          title: 'Verifikacija računa',
        },
        component: () => import('@/components/Auth/VerifyEmail.vue'),
      },
    ],
  },
  {
    path: '/',
    component: MainApp,
    meta: {
      name: 'MainApp',
    },
    beforeEnter: async (to, from, next) => {
      const token = localStorage.getItem('token');

      // if (!token && !to.meta.allowAnonymous) {

      if (!token) {
        next({ name: 'Login' });
        return;
      }

      await store.dispatch('getCategories', {
        sortBy: 'createdDate',
        isSortAscending: false,
        retrieveAll: true,
      });

      // if (!to.meta.allowAnonymous) {
      await store.dispatch('getProfile').catch((error) => {
        // Api respond with 405 on some endpoints when token is not valid
        if (error.response.status === 401 || error.response.status === 405) next({ name: 'Login' });
      });
      // }

      setTimeout(() => {
        next();
      }, 200);
    },
    children: [
      {
        path: '/',
        name: 'Home',
        meta: {
          title: translations.homePageTitle,
        },
        component: () => import(/* webpackChunkName: 'home' */ '@/views/Home.vue'),
      },
      {
        path: '/about',
        name: 'About',
        meta: {
          title: translations.aboutUs,
        },
        component: () => import(/* webpackChunkName: 'about' */ '@/views/About.vue'),
      },
      {
        path: '/complaints',
        name: 'Complaints',
        meta: {
          title: 'Reklamacije',
        },
        component: () => import(/* webpackChunkName: 'complaints' */ '@/views/Complaints.vue'),
      },
      {
        path: '/payment-methods-and-delivery',
        name: 'PaymentMethodsAndDelivery',
        meta: {
          title: 'Nacini placanja i dostava',
        },
        component: () => import(/* webpackChunkName: 'PaymentMethodsAndDelivery' */ '@/views/PaymentMethodsAndDelivery.vue'),
      },
      {
        path: '/security-and-privacy',
        name: 'SecurityAndPrivacy',
        meta: {
          title: 'Izjava o privatnosti',
        },
        component: () => import(/* webpackChunkName: 'SecurityAndPrivacy' */ '@/views/SecurityAndPrivacy.vue'),
      },
      {
        path: '/responsible-business',
        name: 'ResponsibleBusiness',
        meta: {
          title: 'Odgovorno poslovanje',
        },
        component: () => import(/* webpackChunkName: 'ResponsibleBusiness' */ '@/views/ResponsibleBusiness.vue'),
      },
      {
        path: '/terms-of-service',
        name: 'TermsOfService',
        meta: {
          title: 'Uvjeti korištenja i kupovine',
        },
        component: () => import(/* webpackChunkName: 'TermsOfService' */ '@/views/TermsOfService.vue'),
      },
      {
        path: '/how-to-register',
        name: 'HowToRegister',
        meta: {
          title: 'Kako se registrovati',
        },
        component: () => import(/* webpackChunkName: 'HowToRegister' */ '@/views/HowToRegister.vue'),
      },
      {
        path: '/faq',
        name: 'FAQ',
        meta: {
          title: 'Česta pitanja / FAQ',
        },
        component: () => import(/* webpackChunkName: 'FAQ' */ '@/views/FAQ.vue'),
      },
      {
        path: '/help',
        name: 'Help',
        meta: {
          title: 'Pomoć pri kupovini',
        },
        component: () => import(/* webpackChunkName: 'FAQ' */ '@/views/Help.vue'),
      },
      {
        path: '/service',
        name: 'Service',
        meta: {
          title: 'Service',
        },
        component: () => import(/* webpackChunkName: 'FAQ' */ '@/views/Service.vue'),
      },
      {
        path: '/terms-and-conditions',
        name: 'TermsAndConditions',
        meta: {
          title: 'Odredbe i uslovi',
          allowAnonymous: true,
        },
        component: () => import(/* webpackChunkName: 'about' */ '@/views/TermsAndConditions.vue'),
      },
      {
        path: '/brands',
        name: 'Brands',
        meta: {
          title: 'Brendovi',
        },
        component: () => import(/* webpackChunkName: 'brands' */ '@/views/Brands.vue'),
      },
      {
        path: '/article/:id/:slug',
        name: 'Article',
        meta: {
          title: translations.article,
        },
        component: () => import(/* webpackChunkName: 'article' */ '@/components/Article/Article.vue'),
      },
      {
        path: '/search/:search',
        name: 'Search',
        meta: {
          title: translations.search,
        },
        component: () => import(/* webpackChunkName: 'search-view' */ '@/views/SearchView.vue'),
      },
      {
        path: '/category/:id/:slug',
        name: 'Category',
        meta: {
          title: translations.category,
        },
        component: () => import(/* webpackChunkName: 'category' */ '@/views/CategoryView.vue'),
      },
      {
        path: '/wishlist',
        name: 'Wishlist',
        meta: {
          title: translations.wishlist,
        },
        component: () => import(/* webpackChunkName: 'wishlist' */ '@/views/WishList.vue'),
      },
      {
        path: '/shopping-cart',
        name: 'ShoppingCart',
        meta: {
          title: translations.shoppingCart,
        },
        component: () => import(/* webpackChunkName: 'shopping-cart' */ '@/views/ShoppingCart.vue'),
      },
      {
        path: '/checkout',
        name: 'Checkout',
        meta: {
          title: translations.checkout,
          parent: 'ShoppingCart',
        },
        component: () => import(/* webpackChunkName: 'checkout' */'@/views/Checkout.vue'),
      },
      {
        path: '/my-articles',
        name: 'MyArticles',
        meta: {
          title: 'Moji proizvodi',
        },
        component: () => import(/* webpackChunkName: 'my-articles' */'@/views/MyArticles.vue'),
      },
      {
        path: '/user-articles',
        name: 'UserArticles',
        meta: {
          title: translations.userArticles,
        },
        component: () => import(/* webpackChunkName: 'user-articles' */'@/views/UserArticles.vue'),
      },
      {
        path: '/new-articles',
        name: 'NewArticles',
        meta: {
          title: translations.newArticles,
        },
        component: () => import(/* webpackChunkName: 'new-articles' */'@/views/NewArticles.vue'),
      },
      {
        path: '/blogs',
        name: 'Blogs',
        meta: {
          title: translations.news,
        },
        component: () => import(/* webpackChunkName: 'blogs' */ '@/views/Blogs.vue'),
      },
      {
        path: '/blogs/:id/:slug',
        name: 'Blog',
        meta: {
          title: translations.news,
        },
        component: () => import(/* webpackChunkName: 'blogs' */ '@/views/Blog.vue'),
      },
      {
        path: '/contact',
        name: 'Contact',
        meta: {
          title: 'Kontakt',
        },
        component: () => import('@/views/Contact.vue'),
      },
      {
        path: '/akcija',
        name: 'Sales',
        meta: {
          title: 'Artikli na akciji',
        },
        component: () => import(/* webpackChunkName: 'blogs' */ '@/views/Sales.vue'),
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => import(/* webpackChunkName: 'profile' */ '@/views/Profile.vue'),
        meta: {
          title: translations.userProfile,
        },
        redirect: () => ({
          name: 'ProfileDashboard',
        }),
        children: [
          {
            path: 'dashboard',
            name: 'ProfileDashboard',
            meta: {
              title: translations.userProfile,
            },
            component: () => import(/* webpackChunkName: 'profile-dashboard' */ '@/components/Profile/Dashboard.vue'),
          },
          {
            path: 'finance',
            name: 'ProfileFinance',
            meta: {
              title: 'Financije',
            },
            component: () => import(/* webpackChunkName: 'profile-finance' */ '@/components/Profile/Finance.vue'),
          },
          {
            path: 'shopping-cards',
            name: 'ShoppingCards',
            meta: {
              title: 'Košarice',
            },
            component: () => import(/* webpackChunkName: 'profile-shopping-cards' */ '@/components/Profile/ShoppingCards.vue'),
          },
          {
            path: 'account',
            name: 'AccountDetails',
            meta: {
              title: translations.userProfile,
            },
            component: () => import(/* webpackChunkName: 'acount-details' */ '@/components/Profile/AccountDetails.vue'),
          },
          {
            path: 'address',
            name: 'Address',
            meta: {
              title: translations.addresses,
            },
            component: () => import(/* webpackChunkName: 'addresses' */ '@/components/Profile/Addresses.vue'),
          },
          {
            path: 'address/edit/:id',
            name: 'EditAddress',
            meta: {
              title: translations.addresses,
            },
            component: () => import(/* webpackChunkName: 'edit-address' */ '@/components/Profile/EditAddress.vue'),
          },
          {
            path: 'address/new',
            name: 'NewAddress',
            meta: {
              title: translations.newAddress,
            },
            component: () => import(/* webpackChunkName: 'new-address' */ '@/components/Profile/NewAddress.vue'),
          },
          {
            path: 'billing',
            name: 'BillingInfo',
            meta: {
              title: translations.userProfile,
            },
            component: () => import(/* webpackChunkName: 'billing-info' */ '@/components/Profile/Billing.vue'),
          },
          {
            path: 'messages',
            name: 'Messages',
            meta: {
              title: translations.userProfile,
            },
            component: () => import(/* webpackChunkName: 'messages' */ '@/components/Profile/Messages.vue'),
          },
          {
            path: 'orders',
            name: 'Orders',
            meta: {
              title: translations.userProfile,
            },
            component: () => import(/* webpackChunkName: 'orders' */ '@/components/Profile/Orders.vue'),
          },
          {
            path: 'my-files',
            name: 'MyFiles',
            meta: {
              title: translations.myFiles,
            },
            component: () => import(/* webpackChunkName: 'my-files' */ '@/components/Profile/MyFiles.vue'),
          },
          {
            path: 'my-files/new-file',
            name: 'NewDocument',
            meta: {
              title: translations.newDocument,
            },
            component: () => import(/* webpackChunkName: 'new-document' */ '@/components/Profile/NewDocument.vue'),
          },
          {
            path: 'rma',
            name: 'RMA',
            meta: {
              title: translations.userProfile,
            },
            component: () => import(/* webpackChunkName: 'rma' */ '@/components/Profile/RMA.vue'),
          },
          {
            path: ':catchAll(.*)',
            redirect: () => ({
              name: 'ProfileDashboard',
            }),
          },
        ],
      },
      {
        path: '/brand/:id/:title',
        name: 'Brand',
        meta: {
          title: translations.brand,
        },
        component: () => import(/* webpackChunkName: 'brand' */ '@/views/Brand.vue'),
      },
      {
        path: '/:catchAll(.*)',
        redirect: () => ({ name: 'Home' }),
      },
    ],
  },
];

const router = new VueRouter({
  routes,
  mode: 'history',
  linkActiveClass: 'active',
});

router.afterEach((to, from) => {
  document.title = `${to.meta.title} - ${config.appTitle}`;
  localStorage.setItem('prevRouteName', from.name);
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

export default router;
export {
  routes,
};
